/**
 * Web-local TanStack Query client.
 *
 * Wraps the shared defaults with web-specific QueryCache + MutationCache
 * onError handlers that convert `SessionExpiredError` (thrown by apiClient
 * on HTTP 401) into a redirect to /signin. Keeps the shared
 * `@lato/client/lib/queryClient` unchanged so the Excel add-in (which has
 * its own platform assumptions) isn't affected.
 *
 * Also centralizes mutation cache invalidation: mutations declare `meta: { invalidates }`
 * instead of each writing its own `invalidateQueries` call — see `handleMutationSettled` below.
 *
 * The router navigation is wired via `registerRouter` because the router
 * and queryClient need each other — queryClient is in the router's
 * context, and the router is how queryClient's onError redirects. We
 * create queryClient first (no router dependency), then register the
 * router once it's constructed (see `web/src/router.ts`).
 */

import {
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import type { Mutation, QueryKey } from "@tanstack/react-query";
import {
  AccountDeactivatedError,
  SessionExpiredError,
  SsoRequiredError,
} from "@lato/client/lib/apiClient";
import type { AnyRouter } from "@tanstack/react-router";
import { safeSignInReturnPath } from "./sign-in-redirect";

type InvalidatesArg = {
  data: unknown;
  variables: unknown;
  mutation: Mutation<unknown, unknown, unknown>;
};
type Invalidates = readonly QueryKey[] | ((arg: InvalidatesArg) => readonly QueryKey[]);

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * Query keys to invalidate once this mutation settles (success or error). The single
       * place mutations declare cache impact instead of each writing its own invalidateQueries
       * call — see handleMutationSettled below, the only place that call is actually made.
       */
      invalidates?: Invalidates;
    };
  }
}

let registeredRouter: AnyRouter | null = null;

export function registerRouter(router: AnyRouter): void {
  registeredRouter = router;
}

/** Errors after which apiClient has already signed the user out. */
export function isSignedOutError(err: unknown): boolean {
  return (
    err instanceof SessionExpiredError ||
    err instanceof SsoRequiredError ||
    err instanceof AccountDeactivatedError
  );
}

function handleQueryError(err: unknown): void {
  if (!isSignedOutError(err) || !registeredRouter) return;
  // Speculative (route-preload/hover) auth failures left the session intact and
  // must NOT redirect — the user never clicked. The real navigation re-runs the
  // loader and redirects then. (apiClient sets `speculative` on these errors.)
  if ((err as { speculative?: boolean }).speculative) return;
  // The invite page authenticates in place (OAuth back to /invite/{token}), so
  // a 401 there must NOT yank the user to /signin and drop the invite token.
  if (registeredRouter.state.location.pathname.startsWith("/invite/")) return;
  // Concurrent failures must not erase a return destination already captured.
  if (registeredRouter.state.location.pathname === "/signin") return;
  void registeredRouter.navigate({
    to: "/signin",
    search: { redirect: safeSignInReturnPath(registeredRouter.state.location.href) },
  });
}

/**
 * Reads a settled mutation's `meta.invalidates` and performs the actual `invalidateQueries`
 * call — the one place that call is made, instead of every mutation writing its own. Falls back
 * to a dev-mode warning when a mutation declares neither `invalidates` nor its own
 * onSuccess/onSettled, since it's very likely a missed cache-invalidation gap (this project's
 * whole reason for adopting the convention — see web/src/features/studies/queryKeys.ts).
 */
function handleMutationSettled(
  data: unknown,
  _error: unknown,
  variables: unknown,
  _onMutateResult: unknown,
  mutation: Mutation<unknown, unknown, unknown>
): void {
  const invalidates = mutation.options.meta?.invalidates;
  if (!invalidates) {
    if (import.meta.env.DEV && !mutation.options.onSuccess && !mutation.options.onSettled) {
      console.warn(
        "[queryClient] mutation has no meta.invalidates and no onSuccess/onSettled — if it " +
          "changes server state another query reads, add meta.invalidates (or, if it genuinely " +
          "has no cache effect, silence this with meta: { invalidates: [] }).",
        { mutationKey: mutation.options.mutationKey, variables }
      );
    }
    return;
  }
  const keys = typeof invalidates === "function" ? invalidates({ data, variables, mutation }) : invalidates;
  for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
}

/**
 * Retry a failure once if a second attempt can change the outcome. A signed-out request would go
 * out again with no token, and a 4xx answers the same way twice. Errors with no status are dropped
 * connections and the untyped `throw new Error(...)` call sites, so those stay retryable.
 */
export function shouldRetryQuery(failureCount: number, err: unknown): boolean {
  if (failureCount >= 1 || isSignedOutError(err)) return false;
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status !== "number") return true;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Every query on a page fails together when the pool is full, so a fixed delay brings them all back
 * together. Wait at least the pool's own three-second checkout wait, then spread them over as long
 * again, doubling for the few queries that allow more than one retry.
 */
export function queryRetryDelay(attempt: number): number {
  const wait = Math.min(3_000 * 2 ** attempt, 30_000);
  return wait + Math.random() * wait;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      // Waking a laptop fires `online` in every open tab at once, and each tab would refetch every
      // mounted query. Only the tab in front of the user needs that.
      refetchOnReconnect: () => document.visibilityState === "visible",
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
    },
  },
  queryCache: new QueryCache({ onError: handleQueryError }),
  mutationCache: new MutationCache({
    onError: handleQueryError,
    onSettled: handleMutationSettled,
  }),
});
